import React, { createContext, useContext, ReactNode, useEffect } from 'react'
import { Field, LeadState, Manager, Status } from '@/api/types/crm.types'
import { useAsync } from 'react-use'
import { crmApi, wazzupIntegrationApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { WazzupGetFields } from '@pages/Admin/PlatformSettings2/ServiceIntegrations/components/WazzupIntegrationSettings/type'

export interface PreloadingData {
  managers: Manager[]
  statuses: Status[]
  fields: Field[]
}

interface CRMContextType {
  preloadingData: PreloadingData
  currentTab: LeadState
  wazzupIntegrationsData: WazzupGetFields | undefined
}

const CRMContext = createContext<CRMContextType | undefined>(undefined)

interface CRMProviderProps {
  children: ReactNode
  currentTab: LeadState
}

export const CRMProvider: React.FC<CRMProviderProps> = ({
  children,
  currentTab,
}) => {
  const addAlert = useAddAlert()

  const managersState = useAsync(crmApi.getAllManagers, [])
  const fieldsState = useAsync(crmApi.getAllFields, [])
  const statusesState = useAsync(
    async () => await crmApi.getAllStatuses({ type: 'all' }),
    [],
  )

  const preloadingData: PreloadingData = {
    managers: managersState.value?.data?.data || [],
    statuses: statusesState.value?.data?.data || [],
    fields: fieldsState.value?.data?.data || [],
  }

  useEffect(() => {
    if (managersState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки менеджеров',
        type: 'error',
      })
    }
  }, [managersState.error])

  useEffect(() => {
    if (statusesState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки статусов',
        type: 'error',
      })
    }
  }, [statusesState.error])

  useEffect(() => {
    if (fieldsState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка загрузки полей',
        type: 'error',
      })
    }
  }, [fieldsState.error])

  const wazzupIntegrationsState = useAsync(wazzupIntegrationApi.info, [])
  const wazzupIntegrationsData = (wazzupIntegrationsState?.value?.data ??
    undefined) as WazzupGetFields | undefined

  return (
    <CRMContext.Provider
      value={{
        preloadingData,
        currentTab,
        wazzupIntegrationsData,
      }}
    >
      {children}
    </CRMContext.Provider>
  )
}

export const useCheckCRMContext = (): boolean => {
  const context = useContext(CRMContext)
  return context !== undefined
}

export const useCRMContext = (): CRMContextType => {
  const context = useContext(CRMContext)
  if (context === undefined) {
    throw new Error('useCRMContext must be used within a CRMProvider')
  }
  return context
}
